import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Autocomplete, Button, Card, CardContent, CardHeader, Checkbox, Chip, LinearProgress, Stack, TextField, Typography } from '@mui/material';
import { PsychologyOutlined, Refresh } from '@mui/icons-material';
import api from '../../services/api';

export default function ResearchModelsCard() {
  const [models, setModels] = useState<string[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const load = useCallback(async (refresh = false) => {
    setLoading(true); setError(''); setSaved(false);
    try {
      const data = await api.getResearchModelSettings(refresh);
      const ids = data.models.map(m => m.id);
      setModels(ids); setSelected(data.enabled_model_ids === null ? ids : data.enabled_model_ids.filter(id => ids.includes(id)));
    } catch { setError('Gateway models could not be loaded. Refresh to retry.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);
  const save = async () => {
    setBusy(true); setError(''); setSaved(false);
    try {
      const data = await api.updateResearchModelSettings(selected);
      setModels(data.models.map(m => m.id)); setSelected(data.enabled_model_ids || []); setSaved(true);
    } catch { setError('Model selection could not be saved. Refresh the gateway catalog and retry.'); }
    finally { setBusy(false); }
  };
  return <Card id="research-models" variant="outlined"><CardHeader avatar={<PsychologyOutlined color="primary" />} title="Research models"
    subheader="Choose which gateway models users can select for new research runs across all tenants." />
    <CardContent><Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}
      {saved && <Alert severity="success">Research models saved. Users can select only the enabled models.</Alert>}
      {loading && <LinearProgress aria-label="Loading gateway model selection" />}
      <Autocomplete multiple limitTags={5} disableCloseOnSelect options={models} value={selected} disabled={loading || busy}
        onChange={(_, values) => { setSelected(values); setSaved(false); }}
        renderTags={(values, getTagProps) => values.map((value, index) => {
          const { key, ...props } = getTagProps({ index });
          return <Chip key={key} label={value} {...props} />;
        })}
        renderOption={(props, option, { selected: checked }) => {
          const { key, ...rest } = props as typeof props & { key: string };
          return <li key={key} {...rest}><Checkbox checked={checked} tabIndex={-1} sx={{ mr: 1 }} />{option}</li>;
        }}
        renderInput={params => <TextField {...params} label="Enabled research models" placeholder="Search gateway models" helperText={`${selected.length} of ${models.length} gateway models enabled. Model IDs are used exactly as listed.`} />} />
      {!selected.length && !loading && <Alert severity="warning">Saving an empty selection disables new research runs. Existing runs continue with their saved model.</Alert>}
      <Typography variant="body2" color="text.secondary">New gateway models remain unavailable after a selection is saved until you enable them here. Existing research runs are unchanged.</Typography>
      <Stack direction="row" gap={1} flexWrap="wrap">
        <Button variant="contained" disabled={loading || busy || !models.length} onClick={save}>{busy ? 'Saving…' : 'Save enabled models'}</Button>
        <Button disabled={loading || busy} onClick={() => { setSelected(models); setSaved(false); }}>Select all</Button>
        <Button disabled={loading || busy} onClick={() => { setSelected([]); setSaved(false); }}>Clear selection</Button>
        <Button startIcon={<Refresh />} disabled={loading || busy} onClick={() => void load(true)}>Refresh gateway models</Button>
      </Stack>
    </Stack></CardContent>
  </Card>;
}
